import { useState, useEffect } from 'react';
import axios from 'axios';

// Update the API URL to use the complete URL
const API_URL = 'https://didrouteapi.com/api';  // Adjust this URL according to your backend server

export default function LiveCall() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [userId, setUserId] = useState(null);

  // Add user profile fetch
  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://didrouteapi.com/api/users/profile', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const profileData = await response.json();
      setUserId(profileData.id);
      return profileData.id;
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError('Failed to fetch user profile');
      return null;
    }
  };

  const fetchCampaigns = async (currentUserId) => {
    try {
      if (!currentUserId) {
        return;
      }

      const token = localStorage.getItem('token');
      
      const dialogResponse = await axios.get(`${API_URL}/dialogs`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          userId: currentUserId,
          page,
          limit: 10,
        },
      });

      // Validate response data
      if (!dialogResponse.data?.data?.dialogs) {
        setError('Invalid response format');
        setLoading(false);
        return;
      }

      setLoading(false);
      
      const campaignTargetsResponse = await axios.get(`${API_URL}/campaigns/targets`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      
      const campaignTargets = campaignTargetsResponse.data.data;
      
      const transformedData = dialogResponse.data.data.dialogs.map(dialog => {
        const mangledToNumber = dialog.mangled_to_uri?.match(/sip:(\d+)@/)?.[1] || "N/A";
        const didNumber = dialog.to_uri?.match(/sip:(\d+)@/)?.[1] || "N/A";
        const destinationNumber = dialog.mangled_to_uri?.match(/sip:(\d+)@/)?.[1] || "N/A";
        const callerNumber = dialog.mangled_from_uri?.match(/sip:(\d+)@/)?.[1] || "N/A";

        const matchingCampaign = campaignTargets.find(target => {
          const normalizedDID = didNumber ? didNumber.replace(/^0+/, '').trim() : '';
          const normalizedTargetNumber = target.target_number ? target.target_number.replace(/^0+/, '').trim() : '';
          const normalizedCampaignNumber = target.campaign_target_number ? target.campaign_target_number.replace(/^0+/, '').trim() : '';
          
          if (!normalizedDID || (!normalizedTargetNumber && !normalizedCampaignNumber)) {
            return false;
          }

          return normalizedDID === normalizedTargetNumber || 
                 normalizedDID === normalizedCampaignNumber;
        });

        return {
          id: dialog.dlg_id,
          Target_Number: mangledToNumber,
          DID_Number: didNumber,
          Campaign_Name: matchingCampaign ? matchingCampaign.campaign_name : "Unknown Campaign",
          Destination: destinationNumber,
          caller_number: callerNumber,
          Status: "Active",
        };
      });

      setCalls(transformedData);
      setTotalPages(Math.ceil(dialogResponse.data.data.total / 10));
    } catch (err) {
      console.error('Error Details:', {
        error: err,
        response: err.response,
        message: err.message,
        stack: err.stack
      });
      setLoading(false);
      setError(err.response?.data?.error || 'Failed to fetch dialogs');
    }
  };

  // Update useEffect to handle user profile fetch and more frequent updates
  useEffect(() => {
    const init = async () => {
      const id = await fetchUserProfile();
      if (id) {
        await fetchCampaigns(id);
      }
    };

    init();
    
    // Set up interval with 2-second refresh rate
    if (userId) {
      const interval = setInterval(() => fetchCampaigns(userId), 3000); // Changed from 30000 to 2000
      return () => clearInterval(interval);
    }
  }, [page, userId]);

  const handleDisconnect = async (callId) => {
    try {
      setLoading(true); // Add loading state while disconnecting
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/dialogs/${callId}/disconnect`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      if (response.data.success) {
        // Remove the disconnected call from the state immediately
        setCalls(prevCalls => prevCalls.filter(call => call.id !== callId));
      } else {
        setError('Failed to disconnect call');
      }
    } catch (error) {
      console.error('Error disconnecting call:', error);
      setError(error.response?.data?.error || 'Failed to disconnect call');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (callId) => {
    // Implement your action logic here
  };

  // Update the action button in the table
  const renderActionButton = (callId) => (
    <button
      className="text-red-500 hover:text-red-300 disabled:opacity-50"
      onClick={() => handleDisconnect(callId)}
      disabled={loading}
    >
      {loading ? 'Disconnecting...' : 'Disconnect'}
    </button>
  );

  if (loading) {
    return (
      <div className="bg-white p-2 rounded-lg shadow-lg">
        <div className="text-black text-center py-4">Loading...</div>
      </div>
    );


  }

  if (error) {
    return (
      <div className="bg-white p-2 rounded-lg shadow-lg">
        <div className="text-red-500 text-center py-4">{error}</div>
      </div>
    );

  }

  return (
    <div className="bg-white p-2 rounded-lg shadow-lg">
      <div className="mx-auto max-w-full">
        <div className="bg-white py-10">
          <div className="px-4 sm:px-4 lg:px-4">
            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                  Live Calls
                </h1>
              </div>


            </div>
            <div className="mt-4 flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          className="py-3.5 pl-4 pr-3 text-left text-xs font-medium text-black sm:pl-0"
                        >
                          Destination
                        </th>


                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          DID Number
                        </th>


                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Campaign Name

                        </th>

                        
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Caller Number


                        </th>
                        {/* <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Duration


                        </th> */}
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Status


                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Action


                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {calls.map((call) => (
                        <tr key={call.id}>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900 pl-0">
                            {call.Target_Number}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                            {call.DID_Number}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">

                            {call.Campaign_Name}
                          </td>
                          
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                            {call.caller_number}
                          </td>
                          {/* <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">

                            {call.duration}
                          </td> */}
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                            {call.Status}
                          </td>

                          <td className="whitespace-nowrap px-3 py-4 text-xs font-medium text-red-500">
                            {/* {renderActionButton(call.id)} */} Disconnect
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
